import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  ExclamationTriangleIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../../lib/config';

const API = `${API_BASE}`;

const EmployerMessages = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { workerId } = useParams();
  const [activeTab, setActiveTab] = useState('list');
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user || user.user_type !== 'employer') {
      toast.error('事業者権限が必要です');
      navigate('/');
      return;
    }

    fetchChatRooms();
    fetchEmergencyContacts();

    // URL パラメータでworkerId が指定されている場合、そのワーカーとのチャットを開く
    if (workerId) {
      setActiveTab('chat');
      fetchChatWithWorker(workerId);
    }
  }, [user, navigate, workerId]);

  try {
    setLoading(true);

    // 実際の実装では API から取得
    // No endpoint verified yet, setting to empty to clean up dummy data.
    setChatRooms([]);
  } catch (err) {
    console.error('Error fetching chat rooms:', err);
    toast.error('チャットルームの取得に失敗しました');
  } finally {
    setLoading(false);
  }

  const fetchChatWithWorker = async (workerId) => {
    try {
      // 指定されたワーカーとのチャットルームを検索
      const existingRoom = chatRooms.find(room => room.worker_id === workerId);
      if (existingRoom) {
        setSelectedRoom(existingRoom);
        await fetchMessages(existingRoom.id);
      } else {
        // 新しいチャットルームを作成
        const newRoom = {
          id: `chat-new-${workerId}`,
          worker_id: workerId,
          worker_name: 'ワーカー',
          worker_avatar: null,
          job_title: '求人案件',
          work_date: new Date().toISOString().split('T')[0],
          last_message: '',
          last_message_time: new Date().toISOString(),
          unread_count: 0,
          status: 'new'
        };
        setSelectedRoom(newRoom);
        setMessages([]);
      }
    } catch (err) {
      console.error('Error fetching chat with worker:', err);
    }
  };

  const fetchEmergencyContacts = async () => {
    try {
      // 実際の実装では API から取得
      // Setting to empty to clean up dummy data.
      setEmergencyContacts([]);
    } catch (err) {
      console.error('Error fetching emergency contacts:', err);
    }
  };

  const fetchMessages = async (chatRoomId) => {
    try {
      // 実際の実装では API から取得
      setMessages([]);
    } catch (err) {
      console.error('Error fetching messages:', err);
      toast.error('メッセージの取得に失敗しました');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedRoom) return;

    try {
      const newMsg = {
        id: `msg-${Date.now()}`,
        sender_id: user.id,
        sender_name: user.name,
        sender_type: 'employer',
        message: newMessage.trim(),
        sent_at: new Date().toISOString(),
        is_read: false
      };

      setMessages(prev => [...prev, newMsg]);
      setNewMessage('');

      // チャットルームの最終メッセージを更新
      setChatRooms(prev => prev.map(room =>
        room.id === selectedRoom.id
          ? { ...room, last_message: newMsg.message, last_message_time: newMsg.sent_at }
          : room
      ));

      toast.success('メッセージを送信しました');
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('メッセージの送信に失敗しました');
    }
  };

  const handleChatRoomSelect = (room) => {
    setSelectedRoom(room);
    fetchMessages(room.id);
    setActiveTab('chat');
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' });
    }
  };

  const filteredChatRooms = chatRooms.filter(room =>
    room.worker_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    room.job_title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderChatList = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">メッセージ一覧</h3>
          <div className="text-sm text-gray-500">
            {filteredChatRooms.length}件のチャット
          </div>
        </div>
        <input
          type="text"
          placeholder="ワーカー名や求人で検索..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
        {filteredChatRooms.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm ? '検索条件に一致するチャットがありません' : 'チャットがありません'}
          </div>
        ) : (
          filteredChatRooms.map((room) => (
            <div
              key={room.id}
              onClick={() => handleChatRoomSelect(room)}
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-1">
                  <div className="flex-shrink-0">
                    {room.worker_avatar ? (
                      <img
                        src={room.worker_avatar}
                        alt={room.worker_name}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                        <UserIcon className="w-6 h-6 text-gray-600" />
                      </div>
                    )}
                  </div>

                  <div className="ml-3 flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {room.worker_name}
                      </h4>
                      <div className="flex items-center space-x-2">
                        {room.unread_count > 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                            {room.unread_count}
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {formatMessageTime(room.last_message_time)}
                        </span>
                      </div>
                    </div>

                    <p className="text-xs text-gray-600 mt-1">
                      {room.job_title} - {room.work_date}
                    </p>

                    <p className="text-sm text-gray-500 mt-1 truncate">
                      {room.last_message || 'まだメッセージがありません'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderChatRoom = () => (
    <div className="bg-white rounded-lg shadow flex flex-col h-96">
      {selectedRoom ? (
        <>
          {/* チャットヘッダー */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                <UserIcon className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">{selectedRoom.worker_name}</h4>
                <p className="text-xs text-gray-600">
                  {selectedRoom.job_title} - {selectedRoom.work_date}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setActiveTab('list');
                setSelectedRoom(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >

            </button>
          </div>

          {/* メッセージ一覧 */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3">
            {messages.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>まだメッセージがありません</p>
                <p className="text-sm">最初のメッセージを送信してみてください</p>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender_type === 'employer' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${message.sender_type === 'employer'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-900'
                      }`}
                  >
                    <p className="text-sm">{message.message}</p>
                    <p
                      className={`text-xs mt-1 ${message.sender_type === 'employer' ? 'text-blue-100' : 'text-gray-500'
                        }`}
                    >
                      {formatMessageTime(message.sent_at)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* メッセージ入力 */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
            <div className="flex space-x-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="メッセージを入力..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="submit"
                disabled={!newMessage.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <PaperAirplaneIcon className="w-5 h-5" />
              </button>
            </div>
          </form>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <ChatBubbleLeftRightIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">チャットを選択してください</h3>
            <p>左側の一覧からチャットを選択すると、メッセージの送受信ができます。</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderEmergencyContacts = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">当日緊急連絡先</h3>
        <p className="text-sm text-gray-600 mt-1">
          勤務当日に緊急で連絡が必要な場合の連絡先一覧
        </p>
      </div>

      <div className="divide-y divide-gray-200">
        {emergencyContacts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            緊急連絡先がありません
          </div>
        ) : (
          emergencyContacts.map((contact) => (
            <div key={contact.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h4 className="font-medium text-gray-900 mr-3">{contact.worker_name}</h4>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${contact.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {contact.status === 'confirmed' ? 'シフト確定' : '調整中'}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center">
                      <ClockIcon className="w-4 h-4 mr-1" />
                      {contact.work_date} {contact.work_time}
                    </div>
                    <div>
                      勤務: {contact.job_title}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="font-medium text-gray-700">通常連絡先</span>
                        <div className="text-sm text-gray-600">{contact.phone}</div>
                      </div>
                      <a
                        href={`tel:${contact.phone}`}
                        className="flex items-center px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                      >
                        <PhoneIcon className="w-4 h-4 mr-1" />
                        通話
                      </a>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div>
                        <span className="font-medium text-red-700">緊急連絡先</span>
                        <div className="text-sm text-red-600">{contact.emergency_phone}</div>
                      </div>
                      <a
                        href={`tel:${contact.emergency_phone}`}
                        className="flex items-center px-3 py-1 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
                      >
                        <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                        緊急通話
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* ヘッダー */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/employer-dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            事業者ダッシュボードに戻る
          </button>
          <h1 className="text-3xl font-bold text-gray-900">求職者へのメッセージ機能</h1>
          <p className="text-gray-600 mt-2">面接不要でも補足連絡・当日緊急連絡先管理</p>
        </div>

        {/* タブナビゲーション */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('list')}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <ChatBubbleLeftRightIcon className="w-5 h-5 mr-2" />
                メッセージ一覧
              </button>
              <button
                onClick={() => setActiveTab('emergency')}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'emergency'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <PhoneIcon className="w-5 h-5 mr-2" />
                緊急連絡先
              </button>
            </nav>
          </div>
        </div>

        {/* タブコンテンツ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            {activeTab === 'list' ? renderChatList() : renderEmergencyContacts()}
          </div>

          <div>
            {activeTab === 'list' && renderChatRoom()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerMessages;